import {
  findLastAssistantSpeechLine,
  findNextAssistantSpeechLine,
  formatChatScenarioForAgent,
} from "@ui/components/core/ChatScenario/formatForAgent";
import type { ChatScenarioMessage } from "@ui/components/core/ChatScenario/types";

/** Dynamic variable keys — must match the ElevenLabs agent configuration. */
export const SPEECH_ENGINE_PREVIEW_TRANSCRIPT_VAR = "preview_transcript";
export const SPEECH_ENGINE_HANDOFF_INSTRUCTION_VAR = "handoff_instruction";
export const SPEECH_ENGINE_USER_LOCALE_VAR = "user_locale";

/** Short handoff hint injected alongside the preview transcript at voice session start. */
export const SPEECH_ENGINE_HANDOFF_INSTRUCTION =
  "The user just finished reading a scripted preview of this conversation on the Speech Engine marketing demo. Continue the live voice call naturally from the last exchange. Do not restart with a generic greeting or repeat your last preview line verbatim.";

export function buildSpeechEngineHandoffVariables({
  previewTranscript,
  locale,
  handoffInstruction,
}: {
  previewTranscript: string;
  locale: string;
  /** Override the handoff instruction. Falls back to `SPEECH_ENGINE_HANDOFF_INSTRUCTION`. */
  handoffInstruction?: string | null;
}) {
  return {
    [SPEECH_ENGINE_PREVIEW_TRANSCRIPT_VAR]: previewTranscript,
    [SPEECH_ENGINE_HANDOFF_INSTRUCTION_VAR]: handoffInstruction ?? SPEECH_ENGINE_HANDOFF_INSTRUCTION,
    [SPEECH_ENGINE_USER_LOCALE_VAR]: locale,
  };
}

export type SpeechEngineLiveMessage = {
  role: "agent" | "user";
  message: string;
};

export type SpeechEngineHandoffData = {
  transcript: string | null;
  firstMessage: string | null;
  pending: boolean;
};

function convertLiveMessagesToScenario(messages: SpeechEngineLiveMessage[]): ChatScenarioMessage[] {
  return messages.map(message => ({
    role: message.role === "agent" ? "assistant" : "user",
    parts: [{ type: "text", content: message.message }],
  }));
}

export function buildLiveSpeechEngineHandoff(messages: SpeechEngineLiveMessage[]): SpeechEngineHandoffData {
  if (messages.length === 0) {
    return { transcript: null, firstMessage: null, pending: false };
  }

  const scenarioMessages = convertLiveMessagesToScenario(messages);
  const transcript = formatChatScenarioForAgent(
    { messages: scenarioMessages },
    {
      assistantLabel: "El",
      messageLimit: scenarioMessages.length,
    },
  );
  const lastMessage = messages[messages.length - 1];

  if (lastMessage.role === "user") {
    return { transcript, firstMessage: null, pending: true };
  }

  return {
    transcript,
    firstMessage: lastMessage.message.trim() || null,
    pending: false,
  };
}

export function buildScriptedSpeechEngineHandoff({
  messages,
  receivedMessageCount,
}: {
  messages: ChatScenarioMessage[];
  receivedMessageCount: number;
}): SpeechEngineHandoffData {
  const messageLimit = Math.max(0, Math.min(Math.floor(receivedMessageCount), messages.length));

  if (messageLimit === 0) {
    return { transcript: null, firstMessage: null, pending: false };
  }

  const transcript = formatChatScenarioForAgent(
    { messages },
    {
      assistantLabel: "El",
      messageLimit,
    },
  );
  const lastReceivedMessage = messages[messageLimit - 1];
  const firstMessage =
    lastReceivedMessage.role === "assistant"
      ? findLastAssistantSpeechLine(messages, messageLimit)
      : findNextAssistantSpeechLine(messages, messageLimit);

  return {
    transcript,
    firstMessage,
    pending: false,
  };
}
